<template>
  <div v-if="contrat">
    <div class="entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3">
                {{ this.$route.params.id ?
                $t('ajoutEditContrat.title.edit', { code: this.contratCode})
                : $t('ajoutEditContrat.title.add')}}
              </h1>
              <breadcrumb :breadcrumb="breadcrumb"/>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="buttons">
            <button class="button" @click="$router.go(-1)">
              {{ $t('ajoutEditContrat.actions.cancel') }}
            </button>
            <button class="button is-addOrEdit"
                    @click="saveContrat">
              {{ contrat.etat !== isBrouillon ?
                  $t('general.button.sauvegarder') :
                  $t('ajoutEditContrat.actions.brouillon') }}
            </button>
            <piece-jointe-button
              class="mgr-2"
              :perm="permContrat.maj">
            </piece-jointe-button>
          </div>
        </div>
      </div>
      <app-information-msg-component
        v-if="displayMessageInfoRenouvellement"
        :title="'ajoutEditContrat.messages.info.title'"
        :libelleList="[{ islink: false,
          libelle: $t('ajoutEditContrat.messages.info.generationEcheancier')}]"/>
      <div class="tab">
        <ul>
          <li :class="{'is-active': activeTab === 'bail' || activeTab === 'bailDetail'}"
              @click="contrat.etat === contratEtatclos ? activeTab = 'bailDetail'
              : activeTab = 'bail'">
            {{ $t('ajoutEditContrat.tab.bail') }}
          </li>
          <li :class="{'is-active': activeTab === 'materielsAssocies'}"
              @click="activeTab = 'materielsAssocies'">
            {{ $t('ajoutEditContrat.tab.materielsAssocies') }}
          </li>
          <li :class="{'is-active': activeTab === 'produitsAssocies'}"
              @click="activeTab = 'produitsAssocies'"
              v-if="modulePresseActive">
            {{ $t('ajoutEditContrat.tab.produitsAssocies') }}
          </li>
          <li :class="{'is-active': activeTab === 'echeancier'}"
              @click="activeTab = 'echeancier'">
            {{ $t('ajoutEditContrat.tab.echeancier') }}
          </li>
          <li :class="{'is-active': activeTab === 'reglesAchat'}"
              @click="activeTab = 'reglesAchat'"
              v-if="modulePresseActive">
            {{ $t('catalogue.newProduct.tab.regleAchat') }}
          </li>
        </ul>
      </div>
    </div>
    <div>
      <error-msg-component :messages="messages"
                           :messagesChampsVide="messagesChampsVide"
                           :champsVide="champsVide"
                           :error="affichageMessagesErreur"
                           title="ajoutEditContrat.messages.problemOccurred"
                           :withEmplyFields="true"
                           @close="affichageMessagesErreur = false"/>
      <component
          :is="activeTab"
          :contrat="contrat"
          @reloadEcheancier="reloadEcheancier"
          :isViewAjout="contrat.etat !== contratEtatclos"
          :echeancier="echeancier"
          :detail="contrat.etat === contratEtatclos"
          :produit="contrat"
          :lockEcheancier="lockEcheancier">
      </component>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import { CONTRAT } from '@/configuration/baux/permissionsBauxConf';
import bail from '@/components/baux/contrat/componentEdition/bail';
import materielsAssocies from '@/components/baux/contrat/materielsAssocies';
import produitsAssocies from '@/components/baux/contrat/produitsAssocies';
import genererContrat from '@/services/baux/contrat/genererContrat';
import breadcrumb from '@/components/general/breadcrumb';
import PiecesJointes from '@/components/general/piecesJointes';
import {
  create, get, update,
} from '@/api/baux/contrat';
import messageErreur from '@/services/general/messageErreur';
import messageSucces from '@/services/general/messageSucces';
import preparationContratAvantEnvoieBack from '@/services/baux/contrat/preparationContratAvantEnvoieBack';
import transformContrat from '@/services/baux/contrat/transformContrat';
import { syncAddMateriel } from '@/services/baux/contrat/syncMaterielProduitId';
import errorMsgComponent from '@/components/general/errorMsgComponent';
import validation from '@/services/baux/contrat/validation';
import echeancier from '@/components/baux/contrat/componentEdition/echeancier';
import reglesAchat from '@/components/catalogue/produitGeneralPresse/edition/reglesAchat';
import genererEcheancierContrat from '@/services/baux/contrat/genererEcheancierContrat';
import { getEcheancierContrat } from '@/api/commandeAchat';
import preparationEnvoieBackEcheancier from '@/services/baux/contrat/preparationEnvoieBackEcheancier';
import bailDetail from '@/components/baux/contrat/componentDetail/bail';
import { CONTRAT_ETAT_CLOS, CONTRAT_ETAT_BROUILLON } from '@/configuration/baux/contrat/contratConf';
import genererGuid from '@/services/general/genererGuid';
import validationGeneral from '@/services/validation/validation';
import translate from '@/services/baux/contrat/translatorContrat';
import genererIntervenantContrat from '@/services/baux/contrat/genererIntervenantContrat';
import { REMOVE_CONTRAT } from '@/store/modules/baux/contrat-mutation-type';
import AppInformationMsgComponent from '@/components/general/informationMsgComponent';
import { PARAMETRES_KEYS, PARAMETRES_INTERNE_KEYS } from '@/store/modules/parametres-mutation-type';
import pieceJointeButton from '@/components/general/button/pieceJointeButton';
import { copiePieceJointe } from '@/api/pieceJointes/index';

const Attachments = createNamespacedHelpers('attachments');
const societe = createNamespacedHelpers('societe');
const Contrat = createNamespacedHelpers('contrat');
const Parametres = createNamespacedHelpers('parametres');

export default {
  data() {
    return {
      permContrat: CONTRAT,
      contratEtatclos: CONTRAT_ETAT_CLOS,
      activeTab: '',
      contrat: null,
      echeancier: genererEcheancierContrat(),
      messages: [],
      messagesChampsVide: [],
      champsVide: [],
      affichageMessagesErreur: false,
      contratCode: '',
      displayMessageInfoRenouvellement: false,
      isBrouillon: CONTRAT_ETAT_BROUILLON,
      isComingFromStore: false,
      lockEcheancier: [],
    };
  },
  components: {
    bail,
    bailDetail,
    materielsAssocies,
    breadcrumb,
    PiecesJointes,
    errorMsgComponent,
    echeancier,
    reglesAchat,
    AppInformationMsgComponent,
    produitsAssocies,
    pieceJointeButton,
  },
  computed: {
    ...societe.mapGetters({
      societeActiveId: 'societeActiveId',
      societeActive: 'societeActive',
      agencesSelectionnees: 'agenceActive',
    }),
    ...Contrat.mapGetters({
      contratStored: 'contrat',
    }),
    ...Parametres.mapGetters({
      parametre: 'parametre',
      parametresInterne: 'parametresInterne',
    }),
    ...Attachments.mapGetters({
      piecesJointes: 'piecesJointes',
    }),
    modulePresseActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.modulePresse.key);
    },
    breadcrumb() {
      return [
        { title: this.$t('ajoutEditContrat.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$t('ajoutEditContrat.breadcrumb.liste'), route: { name: 'l.baux.contrat' }, isActive: false },
        { title: this.$route.params.id ? this.$t('ajoutEditContrat.breadcrumb.edit') : this.$t('ajoutEditContrat.breadcrumb.add'), route: '', isActive: true },
      ];
    },
    moduleSecteurActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.moduleSecteur.key);
    },
    secteurs() {
      return this.parametre(PARAMETRES_KEYS.secteur);
    },
  },
  methods: {
    ...Attachments.mapActions({
      setAttachmentContext: 'setContext',
      setAttachmentElementId: 'setElementId',
      insertAttachments: 'insert',
      updateAttachments: 'update',
    }),
    ...Parametres.mapActions({
      loadParametres: 'loadParametres',
      loadParametresInterne: 'loadParametresInterne',
    }),
    ...Contrat.mapMutations({
      removeContrat: REMOVE_CONTRAT,
    }),
    async reloadEcheancier(idCommande) {
      const echeancier = (await getEcheancierContrat(this.$route.params.id)).data.result;
      this.lockEcheancier = cloneDeep(echeancier);
      const ligne = cloneDeep(echeancier.find((echeance) => {
        return echeance.devisCommandeId === idCommande;
      }));
      ligne.debutPeriode = moment(ligne.debutPeriode).toDate();
      ligne.finPeriode = moment(ligne.finPeriode).toDate();
      ligne.dateEcheance = moment(ligne.dateEcheance).toDate();
      ligne.id = genererGuid();
      const indexLigne = this.contrat.echeancier.findIndex((echeance) => {
        return echeance.devisCommandeId === idCommande;
      });
      this.contrat.echeancier.splice(indexLigne, 1, ligne);
    },
    genererSecteurLien() {
      return {
        secteurId: this.contrat.secteurId,
        contratId: this.contrat.id,
        id: this.contrat.secteur?.id,
      };
    },
    async saveContrat() {
      this.messages = [];
      this.messagesChampsVide = [];
      this.champsVide = [];
      const loadingComponent = this.$buefy.loading.open();
      try {
        let contrat = cloneDeep(this.contrat);
        preparationContratAvantEnvoieBack(contrat, this.societeActiveId);
        const echeancier = cloneDeep(contrat.echeancier.filter((echeance) => {
          return !echeance.isSousTotal && echeance.tiersId;
        }));
        preparationEnvoieBackEcheancier(echeancier);
        contrat.echeancier = echeancier;
        if (this.moduleSecteurActive) {
          contrat.secteur = this.genererSecteurLien();
        }
        if (this.$route.params.id) {
          await update(contrat);
          await this.updateAttachments(contrat.id);
          messageSucces(this.$i18n.t('ajoutEditContrat.messages.success.edit'));
        } else {
          contrat = (await create(contrat)).data.result;
          if (contrat.contratInitialId) {
            const pjACopierIds = this.piecesJointes.map(pj => pj.id);
            await copiePieceJointe({
              pieceJointeIds: pjACopierIds,
              elementId: contrat.id,
            });
          }
          await this.setAttachmentElementId({ id: contrat.id });
          await this.insertAttachments(contrat.id);
          messageSucces(this.$i18n.t('ajoutEditContrat.messages.success.add'));
        }
        await this.$router.push({ name: 'c.baux.contrat', params: { id: contrat.id } });
        loadingComponent.close();
      } catch (e) {
        loadingComponent.close();
        if (e.response && e.response.data && e.response.data.error) {
          validation(e.response, this.messages, this.contrat.contratsMateriels);
          validationGeneral(this.$i18n, e.response, this.champsVide,
            this.messages, this.messagesChampsVide, translate);
          this.affichageMessagesErreur = true;
          throw e;
        }
        messageErreur(this.$i18n);
        throw e;
      }
    },
    updateInfoEcheancier() {
      this.echeancier.dateDebut = this.contrat.dateDebutEcheance;
      this.echeancier.dateFin = this.contrat.dateFinEcheance;
      this.echeancier.montantAnnuelHt = this.contrat.loyerAnnuelHt;
      this.echeancier.termePaiement = this.contrat.termePaiement;
      this.echeancier.pourcentageTva = this.contrat.tauxTva;
      this.echeancier.quantitePeriodicite = this.contrat.quantitePeriodicite;
      this.echeancier.periodiciteEcheancier = this.contrat.unitePeriodicite;
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.loadParametres([
        PARAMETRES_KEYS.contratNature,
        PARAMETRES_KEYS.secteur,
      ]);
      await this.loadParametresInterne([PARAMETRES_INTERNE_KEYS.moduleSecteur.key]);
      if (!this.societeActive.produitIdDefautLoyer && !this.$route.params.id) {
        messageErreur(this.$i18n, this.$i18n.t('ajoutEditContrat.messages.errors.loyerAnnuelMandatoryCreation'));
      }
      if (this.$route.params.id) {
        if (this.contratStored) {
          this.isComingFromStore = true;
          const contrat = cloneDeep(this.contratStored);
          this.contrat = transformContrat(contrat);
          this.updateInfoEcheancier();
          await syncAddMateriel(this.contrat);
          this.removeContrat();
        } else {
          await this.setAttachmentContext({ module: 'Contrat' });
          if (!this.societeActive.produitIdDefautLoyer) {
            messageErreur(this.$i18n, this.$i18n.t('ajoutEditContrat.messages.errors.loyerAnnuelMandatoryModification'));
          }
          await this.setAttachmentElementId({ id: this.$route.params.id });
          const contrat = (await get(this.$route.params.id)).data.result;
          const echeancier = (await getEcheancierContrat(this.$route.params.id)).data.result;
          echeancier.forEach((echeance) => {
            echeance.debutPeriode = moment(echeance.debutPeriode).toDate();
            echeance.finPeriode = moment(echeance.finPeriode).toDate();
            echeance.dateEcheance = moment(echeance.dateEcheance).toDate();
            echeance.key = genererGuid();
            echeance.selected = false;
          });
          this.lockEcheancier = cloneDeep(echeancier);
          contrat.echeancier = echeancier;
          this.contrat = transformContrat(contrat);
          this.updateInfoEcheancier();
          this.setInformationPage(this.$t('pageGeneral.modification', { item: this.contrat.code }));
        }
      } else {
        if (!this.contratStored) await this.setAttachmentContext({ module: 'Contrat' });
        const contratNatureFind = this.parametre(PARAMETRES_KEYS.contratNature)
          .find((contratNature) => {
            return contratNature.valeurParDefaut;
          });
        this.contrat = genererContrat(contratNatureFind ? contratNatureFind.id : null);
        this.contrat.agenceId = this.agencesSelectionnees[0].agenceId;
        this.setInformationPage(this.$t('pageBaux.contrat.creation'));
        if (this.contratStored) {
          this.isComingFromStore = true;
          const contrat = cloneDeep(this.contratStored);
          this.contrat = transformContrat(contrat);
          this.updateInfoEcheancier();
          await syncAddMateriel(this.contrat);
          this.removeContrat();
          if (!this.contrat.isComingFromAjoutMateriel
             && !this.contrat.isComingFromBailleur) this.displayMessageInfoRenouvellement = true;
        }
      }
      if (this.contrat.etat !== CONTRAT_ETAT_CLOS && this.isComingFromStore === false) {
        this.contrat.intervenants.push(genererIntervenantContrat());
      }
      if (this.moduleSecteurActive && this.contrat.secteur && !this.contrat.secteurId) {
        this.contrat.secteurId = this.contrat.secteur.secteurId;
      }
      this.contratCode = cloneDeep(this.contrat.code);
      if (this.contrat.isComingFromAjoutMateriel) this.activeTab = 'materielsAssocies';
      else this.activeTab = this.contrat.etat === CONTRAT_ETAT_CLOS ? 'bailDetail' : 'bail';
      loadingComponent.close();
    } catch (e) {
      loadingComponent.close();
      messageErreur(this.$i18n);
      throw e;
    }
  },
};
</script>
